import { api } from "./api";

const SW_PATH = "/sw-coach-push.js";

export function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf;
}

export function arrayBufferToBase64Url(buf) {
  if (!buf) return "";
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function detectPushSupport() {
  if (typeof window === "undefined") return { supported: false, iosNotPwa: false };
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isPwa = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
  return { supported, iosNotPwa: isIOS && !isPwa };
}

async function fetchPublicKey() {
  const { data } = await api.get("/coaching/push/vapid-public-key");
  return data?.public_key || "";
}

async function ensureRegistration() {
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  const reg = await navigator.serviceWorker.register(SW_PATH);
  await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((_, rej) => setTimeout(() => rej(new Error("Service worker didn't activate in 10s")), 10000)),
  ]);
  return reg;
}

/**
 * Self-healing sync. Safe to call on every coach page load: when permission
 * is already granted it (re)creates a missing/stale browser subscription
 * WITHOUT prompting and re-registers it with the backend, so a purged or
 * rotated endpoint never leaves the coach silently un-notified.
 *
 * Returns one of: unsupported | ios-not-pwa | unconfigured | denied | idle |
 * subscribed (idle = permission never granted on this device → needs a tap).
 */
export async function syncCoachPush() {
  const det = detectPushSupport();
  if (!det.supported) return det.iosNotPwa ? "ios-not-pwa" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  if (Notification.permission !== "granted") return "idle";
  const publicKey = await fetchPublicKey();
  if (!publicKey) return "unconfigured";
  const reg = await ensureRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (sub) {
    const currentKey = arrayBufferToBase64Url(sub.options?.applicationServerKey);
    if (currentKey && currentKey !== publicKey) {
      try { await sub.unsubscribe(); } catch { /* continue */ }
      sub = null;
    }
  }
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }
  await api.post("/coaching/push/subscribe", { subscription: sub.toJSON() });
  return "subscribed";
}

/** User-gesture path: ask permission (if needed) then sync. Throws on failure. */
export async function enableCoachPush() {
  const det = detectPushSupport();
  if (!det.supported) throw new Error(det.iosNotPwa ? "ios-not-pwa" : "unsupported");
  const publicKey = await fetchPublicKey();
  if (!publicKey) throw new Error("Push notifications aren't configured on the server.");
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return perm === "denied" ? "denied" : "idle";
  }
  return syncCoachPush();
}

export async function getCoachPushStatus() {
  const { data } = await api.get("/coaching/push/status");
  return data;
}
